import React, { useState } from 'react';
import { Search, X, ChevronDown, ChevronUp, Calendar, DollarSign, Star, Hotel, Bed } from 'lucide-react';
import { Button } from './ui';
import api from '../services/api';

const STAR_OPTIONS = ["SELECT ALL", "3 Star", "4 Star", "5 Star", "Boutique", "No Star"];

interface DailyRate {
  date: string;
  rate: number;
  season: string;
}

interface Room {
  room_id: number;
  room_type: string;
  daily_rates: DailyRate[];
  average_rate: number;
}

interface HotelType {
  hotel_id: number;
  hotel_name: string;
  star_rating: string;
  rooms: Room[];
}

const HotelSearchPage: React.FC = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [star, setStar] = useState('SELECT ALL');
  const [hotelName, setHotelName] = useState('');
  const [results, setResults] = useState<HotelType[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expandedHotels, setExpandedHotels] = useState<Set<number>>(new Set());

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      alert('Please select both check-in and check-out dates');
      return;
    }

    // Check date range (max 10 days)
    const daysDiff = Math.ceil((new Date(toDate).getTime() - new Date(fromDate).getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 10) {
      alert('Maximum duration is 10 days');
      return;
    }

    // At least one search criteria must be provided
    if (!hotelName && star === 'SELECT ALL' && (minPrice === 0 && maxPrice === 0)) {
      alert('Please provide at least one search criteria: Price range, Star rating, or Hotel name');
      return;
    }

    setLoading(true);
    try {
      const params: any = {
        from_date: fromDate,
        to_date: toDate,
        star: star,
        hotel_name: hotelName
      };
      
      // Only add price params if they are not 0
      if (minPrice > 0) params.min_price = minPrice;
      if (maxPrice > 0) params.max_price = maxPrice;
      
      const response = await api.get('/hotels/search-rates', { params });
      setResults(response.data);
      setSearched(true);
    } catch (error) {
      console.error('Search failed:', error);
      alert('Failed to search hotels');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFromDate('');
    setToDate('');
    setMinPrice(0);
    setMaxPrice(0);
    setStar('SELECT ALL');
    setHotelName('');
    setResults([]);
    setSearched(false);
    setExpandedHotels(new Set());
  };

  const toggleHotel = (hotelId: number) => {
    const newExpanded = new Set(expandedHotels);
    if (newExpanded.has(hotelId)) {
      newExpanded.delete(hotelId);
    } else {
      newExpanded.add(hotelId);
    }
    setExpandedHotels(newExpanded);
  };

  const getStarDisplay = (rating: string) => {
    const stars = '★'.repeat(parseInt(rating) || 0);
    return stars || rating;
  };

  const formatPrice = (amount: number) => {
    return amount.toLocaleString();
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Search Hotels
          </h1>
          <p className="text-gray-600">Discover the perfect accommodation for your stay</p>
        </div>

        {/* Search Form */}
        <div className={`bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8 mb-8 transition-all duration-300 ${loading ? 'pointer-events-none opacity-60' : ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* Date Inputs */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Calendar className="h-4 w-4 text-indigo-500" />
                Check-in Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Calendar className="h-4 w-4 text-indigo-500" />
                Check-out Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
            {/* Price Range */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <DollarSign className="h-4 w-4 text-green-500" />
                Price Range (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(Number(e.target.value))}
                  min={0}
                  placeholder="Min (optional)"
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                  min={0}
                  placeholder="Max (optional)"
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>
            
            {/* Star Rating */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Star className="h-4 w-4 text-yellow-500" />
                Star Rating
              </label>
              <select
                value={star}
                onChange={(e) => setStar(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {STAR_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            {/* Hotel Name */}
            <div className="md:col-span-2 space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Hotel className="h-4 w-4 text-indigo-500" />
                Hotel Name
              </label>
              <input
                type="text"
                value={hotelName}
                onChange={(e) => setHotelName(e.target.value)}
                placeholder="Search by hotel name..."
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="gradient"
              size="lg"
              icon={loading ? undefined : Search}
              onClick={handleSearch}
              disabled={loading}
              className="px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Searching Hotels...
                </>
              ) : (
                'Search Hotels'
              )}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              icon={X}
              onClick={handleClear}
              disabled={loading}
              className="px-4 py-2 text-sm bg-blue-400 hover:bg-blue-500 text-white rounded-lg"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Results */}
        {searched && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Hotel className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-800">Search Results</h2>
              </div>
              {fromDate && toDate && (
                <p className="text-gray-600 mb-2">
                  📅 {new Date(fromDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} → {new Date(toDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              )}
              <div className="flex flex-wrap gap-4 text-sm">
                {(minPrice > 0 || maxPrice > 0) && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full">
                    <DollarSign className="h-4 w-4" />
                    {minPrice > 0 ? minPrice : 0} - {maxPrice > 0 ? maxPrice : '∞'}
                  </span>
                )}
                <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                  <Hotel className="h-4 w-4" />
                  {results.length} hotel(s) found
                </span>
              </div>
            </div>

            {results.length === 0 ? (
              <div className="text-center py-16">
                <Hotel className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-500 mb-2">No Hotels Found</h3>
                <p className="text-gray-400">Try adjusting your search criteria</p>
              </div>
            ) : (
              <div className="space-y-6">
                {results.map((hotel) => (
                  <div key={hotel.hotel_id} className="bg-gradient-to-r from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
                    <button
                      onClick={() => toggleHotel(hotel.hotel_id)}
                      className="w-full px-6 py-5 bg-gradient-to-r from-indigo-50 to-cyan-50 hover:from-indigo-100 hover:to-cyan-100 flex items-center justify-between transition-all duration-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          <Hotel className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="text-xl font-bold text-gray-800">{hotel.hotel_name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-yellow-500 font-semibold">{getStarDisplay(hotel.star_rating)}</span>
                            <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded-full">
                              {hotel.rooms.length} room type{hotel.rooms.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-xs text-gray-500">From</div>
                          <div className="text-lg font-bold text-indigo-600">
                            {Math.min(...hotel.rooms.map(r => r.average_rate)).toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">View Details</span>
                          {expandedHotels.has(hotel.hotel_id) ? 
                            <ChevronUp className="h-5 w-5 text-gray-400" /> : 
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          }
                        </div>
                      </div>
                    </button>

                    {expandedHotels.has(hotel.hotel_id) && (
                      <div className="p-6 space-y-6 bg-white">
                        {hotel.rooms.map((room) => (
                          <div key={room.room_id} className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-5 border-l-4 border-indigo-400">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <Bed className="h-5 w-5 text-indigo-600" />
                                <h4 className="text-lg font-bold text-gray-800">{room.room_type}</h4>
                              </div>
                              <div className="text-right">
                                <span className="text-sm text-gray-500">Average Rate</span>
                                <div className="text-xl font-bold text-indigo-600">
                                  {formatPrice(room.average_rate)}
                                </div>
                              </div>
                            </div>
                            <div className="overflow-x-auto">
                              <div className="inline-flex flex-wrap gap-2">
                                {room.daily_rates.map((rate, idx) => {
                                  const date = new Date(rate.date);
                                  const dayOfWeek = date.getDay();
                                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                                  
                                  return (
                                    <div key={idx} className={`bg-white rounded-lg p-3 shadow-sm border hover:shadow-md transition-all min-w-[80px] ${
                                      isWeekend ? 'border-blue-200 bg-blue-50' : 'border-gray-100'
                                    }`}>
                                      <div className="text-xs text-gray-500 text-center mb-1">
                                        {dayName}
                                      </div>
                                      <div className="text-sm text-gray-600 text-center mb-1">
                                        {date.getDate()}
                                      </div>
                                      <div className="text-lg font-bold text-gray-900 text-center mb-1">
                                        {formatPrice(rate.rate)}
                                      </div>
                                      {rate.season && (
                                        <div className="text-xs bg-orange-100 text-orange-700 px-1 py-0.5 rounded text-center">
                                          {rate.season.substring(0, 2)}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HotelSearchPage;